'use client'
import { Suspense, useRef, useState } from 'react'
import ErrorBoundary from '@error-boundary'
import dynamic from 'next/dynamic'

import { RaButton, RaContainer, RaHeading } from '@components/core/index.core'
import { SPOTLER_IDS } from '@constants/spotler.constants'
import { useUi } from '@contexts/ui.context'
import { translate } from '@utilities/helpers/core.helpers'
import { getEntityId } from '@utilities/helpers/entity.helpers'
import { useIntersectionObserver } from '@utilities/hooks/useIntersectionObserver.hooks'

import { RaBackgroundMap } from '../RaBackgroundMap/RaBackgroundMap'

import styles from './ra-footer-newsletter.module.scss'

import type { NewsletterInput } from '@/api/trpc/form-inputs'

const RaFormStyles = dynamic(() =>
  import('@components/form-elements/RaFormStyles/RaFormStyles').then(
    ({ RaFormStyles }) => RaFormStyles,
  ),
)

const RaFormTextInput = dynamic(() =>
  import('@components/form-elements/RaFormTextInput/RaFormTextInput').then(
    ({ RaFormTextInput }) => RaFormTextInput,
  ),
)

const RaFormCheckbox = dynamic(() =>
  import('@components/form-elements/RaFormCheckbox/RaFormCheckbox').then(
    ({ RaFormCheckbox }) => RaFormCheckbox,
  ),
)

const RaFormEmailInput = dynamic(() =>
  import('@components/form-elements/RaFormEmailInput/RaFormEmailInput').then(
    ({ RaFormEmailInput }) => RaFormEmailInput,
  ),
)

interface RaFooterNewsletterProps {
  isFamilySite?: boolean
}

export const RaFooterNewsletter = ({ isFamilySite }: RaFooterNewsletterProps) => {
  const elementRef = useRef(null)
  const ui = useUi()
  const { hasIntersected } = useIntersectionObserver(elementRef)
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [optin, setOptin] = useState(false)
  const [checkboxError, setCheckboxError] = useState(null)

  const optinOptions = [
    {
      id: 1,
      text: translate('NEWSLETTER_OPTIN_LABEL'),
      value: 'true',
    },
  ]

  const handleOptInChange = (id, val) => {
    if (val === 'true') {
      setOptin(true)
      if (checkboxError) setCheckboxError(null)
    } else {
      setOptin(false)
    }
  }

  const submitForm = async e => {
    e.preventDefault()
    if (!optin) {
      setCheckboxError([
        {
          key: 1,
          message: translate('FORM_FIELD_REQUIRED'),
        },
      ])
      return
    }

    setLoading(true)
    try {
      const res = await fetch('/api/forms/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityId: getEntityId(),
          isFamilySite,
          email,
          name,
        } satisfies NewsletterInput),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || 'Newsletter subscription failed')
      }
      ui.showToaster({
        severity: 'success',
        title: `${email} ${translate('SUCCESFULLY_ADDED')}`,
      })
    } catch (err) {
      ui.showToaster({
        severity: 'error',
        title: err instanceof Error ? err.message : 'Something went wrong, please try again later',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <RaBackgroundMap ref={elementRef}>
      <ErrorBoundary>
        <RaContainer>
          <div className={styles['ra-footer-newsletter__wrapper']}>
            <RaHeading
              component="h2"
              color="light"
              alignment="center">
              {translate('NEWSLETTER_TITLE')}
            </RaHeading>
            {/* Own boundary, so loading the form chunks does not hide the footer */}
            <div className={styles['ra-footer-newsletter__form']}>
              <Suspense>
                {hasIntersected && (
                  <RaFormStyles theme="light">
                    <form
                      onSubmit={submitForm}
                      data-spotler-id={SPOTLER_IDS.newsletterForm}>
                      <RaFormTextInput
                        id="name"
                        value={name}
                        onChange={setName}
                        label={translate('NEWSLETTER_NAME_PLACEHOLDER')}
                        disabled={loading}
                        required
                        color="light"
                      />
                      <RaFormEmailInput
                        id="email"
                        value={email}
                        onChange={setEmail}
                        label={translate('NEWSLETTER_EMAIL_PLACEHOLDER')}
                        disabled={loading}
                        required
                        color="light"
                      />
                      <RaFormCheckbox
                        label={translate('NEWSLETTER_OPTIN_ARIALABEL')}
                        id="optin"
                        options={optinOptions}
                        onChange={handleOptInChange}
                        disabled={loading}
                        hideFieldsetLegend
                        errors={checkboxError}
                      />
                      <RaButton
                        type="submit"
                        variant="secondary-footer"
                        spotlerId={SPOTLER_IDS.newsletterSubmit}
                        fullWidth
                        disabled={loading}
                        label={translate('NEWSLETTER_SUBMIT_BUTTON')}
                      />
                    </form>
                  </RaFormStyles>
                )}
              </Suspense>
            </div>
          </div>
        </RaContainer>
      </ErrorBoundary>
    </RaBackgroundMap>
  )
}
